import { CommonModule } from "@angular/common";
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import { finalize } from "rxjs/operators";

import { GradeViewStateService } from "./grade-view-state.service";
import { StoreStatusType } from "../../../core/identity/constant/store-status-type.enum";
import { AssetReviewService } from "../asset-review/asset-review.service";
import { GradeColor, gradeColor, gradeLabel } from "../asset-review/grade-colors";
import {
  StoreGradeRecord,
  StoreGradeResponse,
  gradeErrorMessage,
  isRetryable,
  noGradeMessage,
  triggerLabel
} from "../asset-review/store-grade.model";

interface SubscoreRow {
  label: string;
  tooltip: string;
  icon: string;
  grade: string;
}

/**
 * Labels and hover text for the five subscores, in the order the card lists them.
 *
 * Taken verbatim from the Full Breakdown's own subscore tooltips, so the same term is not defined
 * two different ways across the two screens. Four are matTooltip strings on the performance cards;
 * Competitive Impact is bannerStrengthTooltipHtml, which is written long and keeps its markup --
 * the card shows it in full rather than truncating what the other screen explains properly. The
 * glossary is a separate, longer-form set and deliberately not the source here.
 *
 * Rendered with innerHTML, so these are trusted constants: never interpolate anything from the
 * API or the user into them.
 */
const SUBSCORES: { key: keyof StoreGradeRecord; label: string; tooltip: string; icon: string }[] = [
  {
    key: "totalSalesPerformance",
    label: "Total Sales Performance",
    tooltip: "How well a store meets sales goals or standards.",
    icon: "assets/images/bar-chart-square-02.png"
  },
  {
    key: "salesPerSqft",
    label: "Sales Per Square Foot",
    tooltip: "Sales revenue divided by store selling area.",
    icon: "assets/images/line-chart-down-04.png"
  },
  {
    key: "realEstateQuality",
    label: "Real Estate Quality",
    tooltip: "Overall condition and appeal of a retail property.",
    icon: "assets/images/building-01.png"
  },
  {
    key: "bannerStrength",
    label: "Banner Strength",
    tooltip: "A brand’s market presence and customer loyalty.",
    icon: "assets/images/target-05.png"
  },
  {
    key: "competitiveImpact",
    label: "Competitive Impact",
    tooltip:
      "<strong>Competitive Impact</strong> (also called <strong>Competitive Incursion</strong>): " +
      "Measures the effect competitors have on a store's performance.<br>" +
      "This metric leverages proprietary data from our " +
      "<strong>Retail Opening and Closing (ROC)</strong> product, ensuring a unique and reliable " +
      "view of competitive dynamics.",
    icon: "assets/images/bar-chart-square-01.png"
  }
];

/**
 * Asset Grade card in the Store Spotlight panel.
 *
 * Shows the stored grade as soon as a store is opened, lets the analyst step through history,
 * and generates an unsaved preview on demand. It renders stored data only: it never calculates
 * a grade, it just reflects hasGrade and reason.
 *
 * The one eligibility call it makes is whether to offer Generate Fresh Grade to a store that has
 * never been graded -- see storeStatus.
 */
@Component({
  selector: "app-asset-grade-card",
  templateUrl: "./asset-grade-card.component.html",
  styleUrls: ["./asset-grade-card.component.scss"],
  standalone: true,
  imports: [CommonModule]
})
export class AssetGradeCardComponent implements OnChanges {
  @Input() storeUuid: string;

  /**
   * Current status of the store, e.g. OPEN. Passed in by the parent, which has already loaded
   * the store, so the card does not fetch it again.
   *
   * Only decides whether Generate Fresh Grade is offered when there is no stored grade: the
   * grade API will not calculate one for a store that is not open, so the button would be a
   * guaranteed failure. Once a grade exists the button is offered regardless, since the analyst
   * may want today's numbers for a store that has since changed status.
   */
  @Input() storeStatus: string | null = null;

  /** Emits the record to display when the analyst opens the full breakdown. */
  @Output() viewFullBreakdown = new EventEmitter<StoreGradeRecord>();

  response: StoreGradeResponse | null = null;
  activeRecord: StoreGradeRecord | null = null;

  /** Unsaved result of Generate Fresh Grade. Session only, and only for this store. */
  preview: StoreGradeRecord | null = null;

  loading = false;
  generating = false;
  historyOpen = false;

  loadError: string | null = null;
  canRetry = true;

  /**
   * A refresh that failed: the request did not complete. Presented as a problem.
   */
  refreshError: string | null = null;

  /**
   * A refresh that completed and reported why there is no grade. Nothing is broken -- the service
   * answered -- so this is presented as information rather than in the failure treatment.
   */
  refreshNotice: string | null = null;

  constructor(private assetReviewService: AssetReviewService,
              private viewState: GradeViewStateService,
              private host: ElementRef<HTMLElement>) {}

  /**
   * The dropdown floats over the rows beneath it, so it has to be dismissible without picking a
   * record -- otherwise it covers the subscores until the analyst selects something they may not
   * want. Scoped to this card's own controls so a second card on the page cannot hold ours open.
   */
  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent): void {
    if (!this.historyOpen) return;
    const target = event.target as HTMLElement | null;
    const control = target?.closest(".history-button, .history-dropdown");
    if (control && this.host.nativeElement.contains(control)) return;
    this.historyOpen = false;
  }

  @HostListener("document:keydown.escape")
  onEscape(): void {
    this.historyOpen = false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["storeUuid"] && this.storeUuid) {
      // Switching stores drops the previous store's preview and resets the view. Clearing
      // generating matters because an in flight refresh for the previous store will not
      // clear it: that response is ignored once the selection has moved on.
      this.preview = null;
      this.historyOpen = false;
      this.refreshError = null;
      this.refreshNotice = null;
      this.generating = false;

      // Coming back from the Full Breakdown recreates this card, so pick up where it left off
      // rather than refetching and dropping the analyst's preview and history selection. Only
      // ever a hit for the same store: a different marker falls through and reloads.
      const parked = this.viewState.restore(this.storeUuid);
      if (parked) {
        this.response = parked.response;
        this.preview = parked.preview;
        this.activeRecord = parked.activeRecord;
        return;
      }

      this.load();
    }
  }

  /** Parked so the card can be rebuilt unchanged after the Full Breakdown closes. */
  private rememberView(): void {
    if (!this.response) return;
    this.viewState.save({
      storeUuid: this.storeUuid,
      response: this.response,
      preview: this.preview,
      activeRecord: this.activeRecord
    });
  }

  load(): void {
    const requestedUuid = this.storeUuid;
    this.loading = true;
    this.loadError = null;
    this.response = null;
    this.activeRecord = null;

    this.assetReviewService
      .getStoreGrade(requestedUuid)
      .pipe(finalize(() => this.settleIfCurrent(requestedUuid, () => (this.loading = false))))
      .subscribe({
        next: response => {
          // An analyst can click through markers faster than these resolve, so a response for
          // a store that is no longer selected is dropped rather than shown against the wrong
          // store.
          this.settleIfCurrent(requestedUuid, () => {
            this.response = response;
            this.activeRecord = response.hasGrade ? response.current : null;
            this.rememberView();
          });
        },
        error: (error: HttpErrorResponse) => {
          this.settleIfCurrent(requestedUuid, () => {
            this.loadError = gradeErrorMessage(error?.status);
            this.canRetry = isRetryable(error?.status);
          });
        }
      });
  }

  private settleIfCurrent(requestedUuid: string, apply: () => void): void {
    if (this.storeUuid === requestedUuid) {
      apply();
    }
  }

  generateFreshGrade(): void {
    if (this.generating) {
      return;
    }
    const requestedUuid = this.storeUuid;
    this.generating = true;
    this.refreshError = null;
    this.refreshNotice = null;

    this.assetReviewService
      .refreshStoreGrade(requestedUuid)
      .pipe(finalize(() => this.settleIfCurrent(requestedUuid, () => (this.generating = false))))
      .subscribe({
        next: response => {
          // A live recalculation takes several seconds, so the analyst may well have moved to
          // another store by now. A preview belongs to the store it was generated for and is
          // discarded rather than shown against a different one.
          this.settleIfCurrent(requestedUuid, () => {
            if (!response.hasGrade || !response.current) {
              // The service answered; it just has no grade to give. Report its reason as-is.
              this.refreshNotice = noGradeMessage(response.reason);
              return;
            }
            this.preview = response.current;
            this.activeRecord = this.preview;
            this.historyOpen = false;
            this.rememberView();
          });
        },
        error: (error: HttpErrorResponse) => {
          // The card keeps showing whatever it had; a failed refresh never blanks it.
          this.settleIfCurrent(requestedUuid, () => {
            this.refreshError = gradeErrorMessage(error?.status);
          });
        }
      });
  }

  toggleHistory(): void {
    this.historyOpen = !this.historyOpen;
  }

  selectRecord(record: StoreGradeRecord): void {
    this.activeRecord = record;
    this.historyOpen = false;
    this.rememberView();
  }

  openFullBreakdown(): void {
    if (this.activeRecord) {
      this.viewFullBreakdown.emit(this.activeRecord);
    }
  }

  /** Current record first, then older ones. The preview is pinned above them when it exists. */
  get historyRows(): StoreGradeRecord[] {
    if (!this.response || !this.response.hasGrade) {
      return [];
    }
    const stored = this.response.current ? [this.response.current] : [];
    return [...stored, ...(this.response.history || [])];
  }

  get subscores(): SubscoreRow[] {
    const record = this.activeRecord;
    if (!record || !record.hasDetail) {
      return [];
    }
    return SUBSCORES.map(subscore => ({
      label: subscore.label,
      tooltip: subscore.tooltip,
      icon: subscore.icon,
      grade: (record[subscore.key] as { grade: string })?.grade
    }));
  }

  /**
   * True when the active record predates the detail columns. The card then shows the overall
   * grade with a short explanation rather than five empty rows.
   */
  get detailUnavailable(): boolean {
    return !!this.activeRecord && !this.activeRecord.hasDetail;
  }

  get isPreviewActive(): boolean {
    return !!this.activeRecord && this.activeRecord === this.preview;
  }

  get isHistoryActive(): boolean {
    return (
      !!this.activeRecord &&
      !this.isPreviewActive &&
      !!this.response &&
      this.activeRecord !== this.response.current
    );
  }

  get noGradeText(): string {
    return noGradeMessage(this.response?.reason);
  }

  /** True once there is something to show: a stored record, a history selection, or a preview. */
  get hasRecordToShow(): boolean {
    return !!this.activeRecord;
  }

  /** The history dropdown is only meaningful against stored records; a preview is not one. */
  get hasStoredGrade(): boolean {
    return !!this.response && this.response.hasGrade;
  }

  /**
   * Whether an ungraded store can be graded from here.
   *
   * Without this the card is a dead end: a store that has never been calculated shows
   * "Grade not calculated yet" and offers no way to calculate one.
   */
  get canGenerateFirstGrade(): boolean {
    return this.storeStatus === StoreStatusType.OPEN;
  }

  isActive(record: StoreGradeRecord): boolean {
    return this.activeRecord === record;
  }

  isCurrent(record: StoreGradeRecord): boolean {
    return !!this.response && record === this.response.current;
  }

  badge(grade: string | null | undefined): GradeColor {
    return gradeColor(grade);
  }

  badgeText(grade: string | null | undefined): string {
    return gradeLabel(grade);
  }

  label(trigger: string | null | undefined): string {
    return triggerLabel(trigger);
  }
}
